import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { useTheme } from '../../lib/useTheme';
import { useScreen } from '../../lib/useScreen';
import UserAvatar from '../../lib/UserAvatar';
import SharedTaskList from './SharedTaskList';
import AdminNotes from './AdminNotes';

const C = { turquesa:'#5dc1b9', mango:'#FDBE02', coral:'#eb636b', logoGrad:'linear-gradient(135deg, #534AB7, #1D9E75)', triGrad:'linear-gradient(135deg, #5dc1b9, #FDBE02, #eb636b)' };
const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";
const CHANNEL_LABELS = { store:'🏪 Mostrador', online:'📱 Internet' };

export default function ClientWorkspace({ client, adminId, onBack, onViewAs }) {
  const t = useTheme();
  const { isMobile } = useScreen();
  const [section, setSection] = useState('pendientes');
  const [sales, setSales] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!client?.id) return;
    if (section === 'ventas') supabase.from('daily_sales').select('*').eq('user_id', client.id).order('created_at', { ascending:false }).limit(50).then(({ data }) => { if (data) setSales(data); });
    if (section === 'metricas') supabase.from('social_metrics').select('*').eq('user_id', client.id).order('created_at', { ascending:false }).limit(20).then(({ data }) => { if (data) setMetrics(data); });
    if (section === 'entregables') supabase.from('deliverables').select('*').eq('user_id', client.id).order('created_at', { ascending:false }).then(({ data }) => { if (data) setDeliverables(data); });
  }, [client?.id, section]);

  const MENU = [
    { id:'pendientes', emoji:'📋', label:'Pendientes' },
    { id:'entregables', emoji:'📦', label:'Entregables' },
    { id:'ventas', emoji:'📊', label:'Sus ventas' },
    { id:'metricas', emoji:'📈', label:'Sus métricas' },
    { id:'notas', emoji:'📝', label:'Notas reunión' },
  ];

  const todaySales = sales.filter(s => s.sale_date === new Date().toISOString().split('T')[0]);
  const totalSales = sales.reduce((s, v) => s + (v.price || 0), 0);
  const layaways = sales.filter(s => s.payment_status === 'layaway');
  const estadoColor = { entregado:C.turquesa, proceso:C.mango, pendiente:C.coral, pending:'#999' };
  const estadoLabel = { entregado:'Entregado', proceso:'En proceso', pendiente:'Pendiente', pending:'Pendiente' };

  const sidebar = (
    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
      {MENU.map(item => (
        <button key={item.id} onClick={() => { setSection(item.id); setMenuOpen(false); }} style={{
          display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:12,
          border:'none', background: section === item.id ? C.turquesa + '15' : 'transparent',
          color: section === item.id ? C.turquesa : t.text, cursor:'pointer',
          fontFamily:font, fontSize:13, fontWeight: section === item.id ? 600 : 400, textAlign:'left',
        }}>
          <span style={{ fontSize:16 }}>{item.emoji}</span>{item.label}
        </button>
      ))}
      <div style={{ borderTop:`0.5px solid ${t.border}`, margin:'8px 0' }} />
      <button onClick={() => onViewAs && onViewAs(client.id)} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:12, border:'none', background:'transparent', color:C.turquesa, cursor:'pointer', fontFamily:font, fontSize:12, fontWeight:500, textAlign:'left' }}>
        👁 Ver su escritorio
      </button>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:20, flexWrap:'wrap' }}>
        <button onClick={onBack} style={{ background:'none', border:'none', color:C.turquesa, fontSize:14, fontWeight:500, cursor:'pointer', fontFamily:font, padding:0 }}>← Mis Clientes</button>
        <div style={{ flex:1 }} />
        <UserAvatar url={client.avatar_url} name={client.full_name} size={42} />
        <div>
          <p style={{ fontSize:16, fontWeight:600, margin:0 }}>{client.business_name || client.full_name}</p>
          <p style={{ fontSize:12, color:t.textSecondary, margin:0 }}>{client.full_name} · {client.city || ''}</p>
        </div>
      </div>

      {/* Layout */}
      <div style={{ display:'flex', gap:20 }}>
        {/* Sidebar desktop */}
        {!isMobile && <div style={{ width:200, flexShrink:0 }}>{sidebar}</div>}

        {/* Mobile menu button */}
        {isMobile && (
          <button onClick={() => setMenuOpen(!menuOpen)} style={{ position:'fixed', bottom:80, left:16, zIndex:200, width:48, height:48, borderRadius:'50%', background:C.turquesa, color:'#fff', border:'none', fontSize:20, cursor:'pointer', boxShadow:'0 4px 16px rgba(0,0,0,0.2)', display:'flex', alignItems:'center', justifyContent:'center' }}>☰</button>
        )}
        {isMobile && menuOpen && (
          <>
            <div onClick={() => setMenuOpen(false)} style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', zIndex:198 }} />
            <div style={{ position:'fixed', left:0, top:0, bottom:0, width:240, background:t.card, zIndex:199, padding:'20px 14px', boxShadow:'4px 0 20px rgba(0,0,0,0.15)' }}>{sidebar}</div>
          </>
        )}

        {/* Content */}
        <div style={{ flex:1, minWidth:0 }}>
          {section === 'pendientes' && (
            <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:isMobile?'1.25rem':'1.5rem' }}>
              <SharedTaskList clientId={client.id} adminId={adminId} isAdmin={true} clientName={client.full_name} />
            </div>
          )}

          {section === 'entregables' && (
            <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:isMobile?'1.25rem':'1.5rem' }}>
              <p style={{ fontSize:14, fontWeight:500, margin:'0 0 12px' }}>📦 Entregables</p>
              {deliverables.length === 0 ? (
                <p style={{ fontSize:13, color:t.textSecondary, textAlign:'center', padding:12 }}>Sin entregables registrados.</p>
              ) : deliverables.map(e => {
                const sc = estadoColor[e.status] || '#999';
                return (
                  <div key={e.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:`0.5px solid ${t.border}` }}>
                    <div style={{ flex:1 }}>
                      <p style={{ fontSize:13, fontWeight:500, margin:0 }}>{e.title}</p>
                      <p style={{ fontSize:11, color:t.textSecondary, margin:0 }}>{e.delivery_date || 'Sin fecha'}</p>
                    </div>
                    <span style={{ fontSize:10, padding:'2px 8px', borderRadius:10, background:sc+'18', color:sc, fontWeight:600 }}>{estadoLabel[e.status] || e.status}</span>
                    {e.link_url && <a href={e.link_url} target="_blank" rel="noopener noreferrer" style={{ fontSize:11, color:C.turquesa }}>Ver</a>}
                  </div>
                );
              })}
            </div>
          )}

          {section === 'ventas' && (
            <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:isMobile?'1.25rem':'1.5rem' }}>
              <p style={{ fontSize:14, fontWeight:500, margin:'0 0 4px' }}>📊 Ventas de {client.business_name}</p>
              <div style={{ display:'flex', gap:16, marginBottom:16, flexWrap:'wrap' }}>
                <div><p style={{ fontSize:11, color:t.textSecondary, margin:0 }}>Total registrado</p><p style={{ fontSize:20, fontWeight:600, margin:0 }}>${totalSales.toLocaleString()}</p></div>
                <div><p style={{ fontSize:11, color:t.textSecondary, margin:0 }}>Ventas hoy</p><p style={{ fontSize:20, fontWeight:600, margin:0, color:C.turquesa }}>{todaySales.length}</p></div>
                {layaways.length > 0 && <div><p style={{ fontSize:11, color:t.textSecondary, margin:0 }}>Apartados</p><p style={{ fontSize:20, fontWeight:600, margin:0, color:C.mango }}>{layaways.length}</p></div>}
              </div>
              {sales.length === 0 ? (
                <p style={{ fontSize:13, color:t.textSecondary, textAlign:'center', padding:12 }}>Sin ventas registradas.</p>
              ) : sales.slice(0, 20).map(s => (
                <div key={s.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', borderBottom:`0.5px solid ${t.border}` }}>
                  <span style={{ fontSize:12 }}>{s.payment_status === 'layaway' ? '🔒' : (CHANNEL_LABELS[s.channel]?.split(' ')[0] || '💰')}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <span style={{ fontSize:13 }}>{s.customer_name ? `${s.customer_name} — ` : ''}{s.product_name}</span>
                    <p style={{ fontSize:10, color:t.textSecondary, margin:0 }}>{s.folio} · {s.sale_date}</p>
                  </div>
                  <span style={{ fontSize:13, fontWeight:500, color: s.payment_status === 'layaway' ? C.mango : C.turquesa }}>${(s.price || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}

          {section === 'metricas' && (
            <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:isMobile?'1.25rem':'1.5rem' }}>
              <p style={{ fontSize:14, fontWeight:500, margin:'0 0 12px' }}>📈 Métricas de {client.business_name}</p>
              {metrics.length === 0 ? (
                <p style={{ fontSize:13, color:t.textSecondary, textAlign:'center', padding:12 }}>Sin métricas registradas.</p>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(4,1fr)', gap:10 }}>
                  {metrics.slice(0, 4).map((m, i) => (
                    <div key={i} style={{ background:t.isDark?'#222':'#f8f8f6', borderRadius:12, padding:12, textAlign:'center' }}>
                      <p style={{ fontSize:10, color:t.textSecondary, margin:'0 0 2px', textTransform:'capitalize' }}>{m.platform}</p>
                      <p style={{ fontSize:18, fontWeight:600, margin:'0 0 2px' }}>{(m.followers || 0).toLocaleString()}</p>
                      <p style={{ fontSize:10, color:t.textSecondary, margin:0 }}>seguidores</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {section === 'notas' && (
            <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:isMobile?'1.25rem':'1.5rem' }}>
              <AdminNotes clientId={client.id} adminId={adminId} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
